import { ee } from '../../services/earth-engine';
import { computeBearing, computeDisplacement } from '../geodesy';
import { INTERNALS } from '../../common/metadata';

/**
 * @function offsetMapper
 * @description Returns a mapper that, given a distance offset along the baseline direction theta,
 * builds a single transect: displaces from origin by offset to find the transect's centre, then
 * displaces perpendicularly (±extent/2) from that centre to get the transect's two endpoints.
 * @param {ee.Number} extent - Total length of the transect (in meters).
 * @param {ee.List} origin - [lng, lat] of the baseline segment's starting point (the previous vertex, b).
 * @param {ee.Number} theta - Bearing of the baseline segment, reversed (see transectAccumulator).
 * **Called by:
 * @see {transectAccumulator}
 * @memberof module:Shoreline
 * @returns {Function} Mapper (offset) => ee.Feature, a transect LineString with its endpoints stored in INTERNALS.
 */
const offsetMapper = (extent, origin, theta) => {
  return (offset) => {
    const centre = computeDisplacement(origin.get(0), origin.get(1), theta, offset);

    const h = extent.divide(2);

    const alpha = theta.add(Math.PI * 0.5);
    const beta = alpha.add(Math.PI);

    const [alphaLng, alphaLat] = computeDisplacement(centre[0], centre[1], alpha, h);
    const [betaLng, betaLat] = computeDisplacement(centre[0], centre[1], beta, h);

    const geometry = ee.Geometry.LineString([betaLng, betaLat, alphaLng, alphaLat]);

    return ee.Feature(geometry, {
      [INTERNALS]: { endpoints: geometry.coordinates() },
    });
  };
};

/**
 * @function transectAccumulator
 * @description Returns an `iterate` reducer that walks the baseline vertex-by-vertex, placing
 * evenly-spaced (every `step` meters) perpendicular transects of length `extent` along each segment.
 * 1. Computes the reversed bearing and length (hypot) of the segment between the previous vertex (b)
 *    and the current vertex (a).
 * 2. Picks offsets along the segment starting from `step - previousRemainder` (to keep spacing
 *    continuous across segments) up to the segment length, spaced by `step`.
 * 3. Builds one transect per offset via offsetMapper.
 * 4. Carries the leftover distance (remainder) into the next segment: if no offset fit in this
 *    segment, the remainder accumulates (previousRemainder + hypot); otherwise it's the distance
 *    from the last placed offset to the end of the segment.
 * @param {ee.Number} step - Distance between transects (in meters).
 * @param {ee.Number} extent - Total length of each transect (in meters).
 * @see {@link offsetMapper} - builds each individual transect feature.
 * **Called by:
 * @see {generateOrthogonalTransects}
 * @memberof module:Shoreline
 * @returns {Function} Reducer (current, last) => updated accumulator list, for ee.List#iterate.
 */
const transectAccumulator = (step, extent) => {
  step = ee.Number(step);
  extent = ee.Number(extent);
  return (current, last) => {
    const list = ee.List(last);
    const lastElement = ee.Dictionary(list.get(-1));
    const a = ee.List(current);
    const b = ee.List(lastElement.get('a'));
    const theta = computeBearing(a.get(0), a.get(1), b.get(0), b.get(1)).add(Math.PI);
    const hypot = ee.Geometry.Point(a).distance(ee.Geometry.Point(b));
    const previousRemainder = ee.Number(lastElement.get('remainder'));
    const offsets = ee.List.sequence(step.subtract(previousRemainder), hypot, step);
    const transects = offsets.map(offsetMapper(extent, b, theta));
    const rawRemainder = ee.Algorithms.If(
      ee.Algorithms.IsEqual(offsets.size(), 0),
      previousRemainder.add(hypot),
      hypot.subtract(offsets.get(-1)),
    );
    const remainder = ee.Number(rawRemainder);
    return list.add(
      ee.Dictionary({
        a,
        b,
        remainder,
        transects,
        theta,
        hypot,
        offsets,
      }),
    );
  };
};

/**
 * @function generateOrthogonalTransects
 * @description Generates evenly-spaced transects perpendicular to the baseline.
 * @param {Array} coordinates - Baseline coordinates as array of [lng, lat] pairs
 * @param {Number} step - Distance between transects (in meters)
 * @param {Numer} extent - Total length of each transect (in meters)
 * @see {@link transectAccumulator} creates dictionary info for each transect
 * @example - return ee obg, for each transect:
 * {
 *  "type": "Feature",
 *  "geometry": {
 *  "type": "LineString",
 *  "coordinates": [
 *  [ 8.304865373294547, 54.87963366669361],
 *  [ 8.27987030176564, 54.880161224989976  ]
 *    ]
 *  },
 *  "id": "0",
 *  "properties": {
 *    "cassie:internals": {
 *      "endpoints": [
 *        [ 8.304865373294547,54.87963366669361],
 *        [8.27987030176564,54.880161224989976]
 *      ]
 *    ...
 * **Called by:
 * @see {performAnalysis}
 * @memberof module:Shoreline
 * @returns {ee.List} list of feature collection
 */
export const generateOrthogonalTransects = (coordinates, step, extent) => {
  coordinates = ee.List(coordinates);
  const first = ee.Dictionary({ a: ee.List(coordinates.get(0)), remainder: 0, transects: [] });
  const result = coordinates.slice(1).iterate(transectAccumulator(step, extent), [first]);
  const transects = ee
    .List(result)
    .map((dict) => ee.Dictionary(dict).get('transects'))
    .flatten();
  const fc = ee.FeatureCollection(transects);
  return fc.toList(fc.size());
};

/**
 * @function expandHorizontally
 * @description Expands each transect (a 2-point LineString) into a rectangle polygon by displacing
 * both endpoints perpendicularly by ±amount/2, producing a buffer strip around each transect used
 * for visualization.
 * @param {ee.List|ee.FeatureCollection} transects - Transect LineString features to expand.
 * @param {ee.Number} amount - Total width of the resulting rectangle (in meters).
 * **Called by:
 * @see {performAnalysis}
 * @memberof module:Shoreline
 * @returns {ee.List} Rectangle Polygon features, one per input transect, preserving its properties.
 */
export const expandHorizontally = (transects, amount) => {
  amount = ee.Number(amount).divide(2);

  const mapper = (transect) => {
    transect = ee.Feature(transect);

    const coords = transect.geometry().coordinates();
    const a = ee.List(coords.get(0));
    const b = ee.List(coords.get(1));

    const theta = computeBearing(a.get(0), a.get(1), b.get(0), b.get(1)).add(Math.PI);
    const alpha = theta.add(Math.PI * 0.5);
    const beta = theta.subtract(Math.PI * 0.5);

    const a1 = computeDisplacement(a.get(0), a.get(1), alpha, amount);
    const a2 = computeDisplacement(a.get(0), a.get(1), beta, amount);

    const b1 = computeDisplacement(b.get(0), b.get(1), alpha, amount);
    const b2 = computeDisplacement(b.get(0), b.get(1), beta, amount);

    const rectangle = ee.Feature(ee.Geometry.Polygon([[a1, a2, b2, b1]]));
    return rectangle.copyProperties(transect);
  };

  return transects.map(mapper);
};
